import { useMemo } from 'react';
import { X } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { MultiSelect } from '@/components/ui/multi-select';

interface TagFilterBarProps {
  /** Tutti i tag presenti nel dataset correntemente visibile. */
  availableTags: string[];
  selectedTags: string[];
  onChange: (tags: string[]) => void;
}

/**
 * Filtro per tag della libreria: una tendina di ricerca, e a seguire su una sola riga i
 * chip cliccabili — scuri quelli attivi (con la X per spegnerli), grigi quelli
 * disponibili. La tendina sta in testa, in posizione fissa: è l'unico elemento che non
 * scorre via, quindi resta il punto d'accesso ai tag che non ci stanno in larghezza.
 *
 * Vincolo che guida il layout: la riga resta **una**. Con `flex-wrap` i tag di una
 * libreria cresciuta occupavano tre o quattro righe e spingevano giù la tabella; qui la
 * riga scorre in orizzontale e la tendina fa da via d'accesso completa e cercabile, così
 * l'altezza dei filtri non dipende da quanti tag esistono.
 *
 * I selezionati sono ordinati per primi: con lo scorrimento orizzontale, un tag attivo
 * finito fuori vista sarebbe un filtro invisibile. I chip saltano di posizione al click,
 * ma è il prezzo per avere i filtri attivi sempre a sinistra, sempre visibili.
 */
export function TagFilterBar({ availableTags, selectedTags, onChange }: TagFilterBarProps) {
  const { t } = useI18n();

  const toggleTag = (tag: string) => {
    onChange(
      selectedTags.includes(tag)
        ? selectedTags.filter((existing) => existing !== tag)
        : [...selectedTags, tag]
    );
  };

  // I selezionati mantengono l'ordine di scelta, i restanti quello (alfabetico) in
  // arrivo. Un tag selezionato che sparisce dai disponibili (perché la ricerca testuale
  // ha ristretto il dataset) resta comunque in riga: è ancora un filtro attivo.
  const orderedTags = useMemo(
    () => [...selectedTags, ...availableTags.filter((tag) => !selectedTags.includes(tag))],
    [availableTags, selectedTags]
  );

  const tagOptions = useMemo(
    // I tag sono stringhe libere (vedi TagInput): value e label coincidono.
    () => orderedTags.map((tag) => ({ value: tag, label: tag })),
    [orderedTags]
  );

  if (orderedTags.length === 0) return null;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <MultiSelect
        compact
        options={tagOptions}
        value={selectedTags}
        onChange={onChange}
        placeholder={t('library.tagsFilterLabel')}
        searchPlaceholder={t('library.tagsFilterSearchPlaceholder')}
        emptyLabel={t('library.tagsFilterEmptyState')}
        clearLabel={t('library.clearTagsFilter')}
        removeLabel={t('library.tagInputRemoveTag')}
        className="shrink-0"
      />

      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-1">
        {orderedTags.map((tag) => {
          const isSelected = selectedTags.includes(tag);
          return (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              aria-pressed={isSelected}
              className={`flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs transition-colors ${
                isSelected
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              {tag}
              {/* La X non è un pulsante annidato (HTML non valido): il chip intero è già
                  il toggle, quindi qui basta l'icona come segnale visivo di "spegnibile". */}
              {isSelected && <X className="h-3 w-3" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
